import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["_bodyElement", "className", "data-test-id", "hideTooltip", "iconNode", "maxChars", "maxTooltipWidth", "text", "tileSize", "textSize", "useOnDark", "hoverStyleTokens"];
import UIIcon from 'UIComponents/icon/UIIcon';
import styled from 'foundations-theming/utils';
import { memoize } from 'underscore';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { TruncateMultiline } from 'foundations-components/transitional/components/TruncateMultiline';
import { useMapTokenPropsToCSS } from 'foundations-components/hooks/useMapTokenPropsToCSS';
import { filterTokenProps } from 'foundations-components/utils/filterTokenProps';
import { View } from 'foundations-components/components/View';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import EPDragHandle from './EPDragHandle';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DEFAULT_MAX_CHARS = 23;
const DEFAULT_TILE_SIZE = 104;
const DEFAULT_FONT_SIZE = 12;
const Wrapper = styled.div.withConfig({
  displayName: "EPAddableTile__Wrapper",
  componentId: "sc-5ya9zr-0"
})([".hs-private.hs-private.hs-private &{", ";cursor:grab;width:", ";height:", ";}.hs-private.hs-private.hs-private &:hover{", "}"], ({
  $cssStyles
}) => $cssStyles, ({
  $tileSize
}) => `${$tileSize}px`, ({
  $tileSize
}) => `${$tileSize}px`, ({
  $cssHoverStyles
}) => $cssHoverStyles);
const IconWrapper = styled.div.withConfig({
  displayName: "EPAddableTile__IconWrapper",
  componentId: "sc-5ya9zr-1"
})([".hs-private.hs-private.hs-private &{", ";line-height:normal;text-align:center;}"], ({
  $cssStyles
}) => $cssStyles);
const makeDetailText = memoize(fontSize => styled.div.withConfig({
  displayName: "EPAddableTile__makeDetailText",
  componentId: "sc-5ya9zr-2"
})([".hs-private.hs-private.hs-private &{font-size:", "px !important;line-height:normal;text-align:center;width:100%;}"], fontSize));
const EPAddableTile = _ref => {
  let {
      _bodyElement,
      className,
      'data-test-id': dataTestId,
      hideTooltip = false,
      iconNode,
      maxChars = DEFAULT_MAX_CHARS,
      maxTooltipWidth = 130,
      text,
      tileSize = DEFAULT_TILE_SIZE,
      textSize = DEFAULT_FONT_SIZE,
      useOnDark = false,
      hoverStyleTokens = {}
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    tokenProps
  } = filterTokenProps(rest);
  const tileStyleTokens = Object.assign({
    backgroundColor: useOnDark ? 'fill-surface-default' : 'fill-field-default',
    borderColor: 'border-core-default',
    borderRadius: '100',
    borderStyle: 'solid',
    borderWidth: '100',
    paddingBlock: '200'
  }, tokenProps);
  const tileCssStyles = useMapTokenPropsToCSS(tileStyleTokens);
  const tileHoverCssStyles = useMapTokenPropsToCSS(hoverStyleTokens);
  const iconWrapperStyleTokens = {
    color: 'icon-core-subtle',
    marginBlockStart: tileSize < DEFAULT_TILE_SIZE ? '250' : '300'
  };
  const iconWrapperCssStyles = useMapTokenPropsToCSS(iconWrapperStyleTokens);
  const disableTooltip = hideTooltip || Boolean(text && maxChars && text.length <= maxChars);
  const icon = iconNode || /*#__PURE__*/_jsx(UIIcon, {
    name: 'customModules'
  });
  return /*#__PURE__*/_jsx(Wrapper, {
    className: className,
    "data-test-id": dataTestId,
    $cssStyles: tileCssStyles,
    $cssHoverStyles: tileHoverCssStyles,
    $tileSize: tileSize,
    children: /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      blockSize: "100%",
      flexDirection: "column",
      children: [/*#__PURE__*/_jsx(Text, {
        as: "div",
        textAlign: "center",
        children: /*#__PURE__*/_jsx(EPDragHandle, {
          color: "icon-core-subtle"
        })
      }), /*#__PURE__*/_jsx(IconWrapper, {
        $cssStyles: iconWrapperCssStyles,
        children: icon
      }), text && /*#__PURE__*/_jsx(UITooltip
      // @ts-expect-error untyped prop
      , {
        _bodyElement: _bodyElement,
        autoPlacement: false,
        delay: 0,
        disabled: disableTooltip,
        placement: "bottom",
        title: text,
        maxWidth: `${maxTooltipWidth}px`,
        children: /*#__PURE__*/_jsx(View, {
          marginBlockStart: "auto",
          children: /*#__PURE__*/_jsx(TruncateMultiline, {
            alignItems: "center",
            blockSize: "22px",
            display: "flex",
            maxChars: maxChars,
            paddingInline: "100",
            paddingBlockStart: tileSize < DEFAULT_TILE_SIZE ? '100' : '0',
            Paragraph: makeDetailText(textSize),
            tagName: "div",
            text: text
          })
        })
      })]
    })
  });
};
export default EPAddableTile;